import { Loader } from '@googlemaps/js-api-loader';

let maps = {}; // Object to hold map instances by ID
const infowindows = [];

export const initGoogleLoader = () => {
	return new Loader({
		apiKey: igniteJsVars.mapsApiKey,
		version: "weekly"
	});
}

export const initGooglePlacesSearch = (searchInput, searchData, searchSubmit, Autocomplete) => {
	const boundsCenter = {
		lat: 39.8097343,
		lng: -98.5556199,
	};

	const options = {
		fields: ["formatted_address", "geometry", "name"],
		bounds:{
			north: boundsCenter.lat + 1,
			south: boundsCenter.lat - 1,
			east: boundsCenter.lng + 1,
			west: boundsCenter.lng - 1,
		},
		strictBounds: false,
	};

	let searchAutoComplete = new Autocomplete(searchInput, options);

	searchAutoComplete.addListener('place_changed', () => {
		const selectedPlace = searchAutoComplete.getPlace();

		searchData.dataset.lat = selectedPlace.geometry.location.lat();
		searchData.dataset.lng = selectedPlace.geometry.location.lng();
		
		// Dispatch click event on searchSubmit to show results
		searchSubmit.dispatchEvent(new Event('click'));
	});	
}

export const initGoogleMapFunctions = async (googleLibraries, mapElem) => {
	const LatLngBounds = googleLibraries[0].LatLngBounds;
	const LatLng = googleLibraries[0].LatLng;
	const Map = googleLibraries[1].Map;
	const InfoWindow = googleLibraries[1].InfoWindow;
	const AdvancedMarkerElement = googleLibraries[2].AdvancedMarkerElement;
	
	// Get the center and bounds
	const locationsData = await getMapLocationsData(mapElem);
    const { center, bounds } = calculateCenterAndZoom(locationsData, LatLngBounds, LatLng);

	const mapOptions = {
		center: center,
		zoom: 14,
		mapTypeControl: false,
		fullscreenControl: false,
		streetViewControl: false,
		zoomControl: false,
		mapId: "d190381952bf1287", // Tied to Alex's Google account as you can't use the styles arry with advanced markers 🤷‍♂️					
	};

	const map = await new Map(mapElem, mapOptions);

	// Fit the map to the bounds
    map.fitBounds(bounds);

	// Add the map markers and info windows
	const markers =await addMapMarkers(map, locationsData, AdvancedMarkerElement, InfoWindow);

	// Store the map instance in the maps object
	const mapId = mapElem.getAttribute('id');
    maps[mapId] = { map, LatLngBounds, LatLng, markers };

	// Update the map instance
	updateMap(mapElem);
};

const getMapLocationsData = async (mapElem) => {
	const activeRegions = mapElem.closest('.js-loc-search-results-container').dataset.activeRegions;
	const activeLocations = mapElem.closest('.js-loc-search-results-container').dataset.activeLocations;
	let locationsData;

	if ( activeRegions || activeLocations ) {
		if ( activeRegions ) {
			locationsData = await getFilteredMapLocationsData(mapElem, 'regions', activeRegions);
		}
		if ( activeLocations ) {
			locationsData = await getFilteredMapLocationsData(mapElem, 'locations', activeLocations);
		}
	} else {
		locationsData = await getFilteredMapLocationsData(mapElem);
	}

	return locationsData;
}

const addMapMarkers = async (map, locationsData, AdvancedMarkerElement, InfoWindow) => {
	const markers = [];

	if ( locationsData ) {
		locationsData.forEach(location => {
			const marker = new AdvancedMarkerElement({
				map,
				content: buildMapMarkerContent(location),
				position: location.position,
				title: location.title,
			});
	
			const infowindow = markerInfoWindow(location, InfoWindow);
			infowindows.push(infowindow);
			
			// Marker click events
			marker.addListener('click', () => {
				// Loop through the infowindows and close any open ones
				infowindows.forEach(iw => {
					iw.close();
				});

				// Open the infowindow elem. new JS Maps API doesn't support mouseove for this yet so using click instead
				infowindow.open({
					anchor: marker,
					map,
				});
			});	

			markers.push({id: location.id, marker: marker});
		});
	}

	return markers;
}

const getFilteredMapLocationsData = async (mapElem, filter = null, filterItems) => {
	const items = mapElem.closest('.js-loc-search-results-map').querySelectorAll('.js-loc-search-map-loc');
	const itemsData = [];
	const filterItemsArr = filter ? filterItems.split(',') : null;

	if ( items ) {
		items.forEach(item => {
			const itemObj = {
				id: item.getAttribute('data-marker-id'),
                region: item.getAttribute('data-region-id'),
				title: item.getAttribute('data-title'),
				url: item.getAttribute('data-url'),
				directions: item.getAttribute('data-directions'),
				position: {
					lat: parseFloat(item.getAttribute('data-lat')),
					lng: parseFloat(item.getAttribute('data-lng')),
				},
			};

			// Filter the items based on the filter type
			if ( filter === 'regions' ) {
				if ( filterItemsArr.includes(itemObj.region) ) {
					itemsData.push(itemObj);
				}
			}
			if ( filter === 'locations' ) {
				if ( filterItemsArr.includes(itemObj.id) ) {
					itemsData.push(itemObj);
				}
			}

			// If no filter, add all items
			if ( !filter ) {
				itemsData.push(itemObj);
			}
		});
	}

	return itemsData;
};

const calculateCenterAndZoom = (locationsData, LatLngBounds, LatLng) => {
    // Create a new bounds object
    let bounds = new LatLngBounds();

    // Extend the bounds to include each location
    locationsData.forEach(location => {
        let latLng = new LatLng(location.position.lat, location.position.lng);
        bounds.extend(latLng);
    });

    // Calculate the center of the bounds
    let center = bounds.getCenter();

    return {
        center: { lat: center.lat(), lng: center.lng() },
        bounds: bounds
    };
}

export const updateMap = async (mapElem) => {
	const mapId = mapElem.getAttribute('id');
    const mapObj = maps[mapId];

    if (!mapObj) {
        console.error(`Map with ID ${mapId} not found.`);
        return;
    }

    // Get the new center and bounds
	const locationsData = await getMapLocationsData(mapElem);
	updateMapMarkers(mapObj, locationsData);
	
    // Update the map center and bounds
    const { center, bounds } = calculateCenterAndZoom(locationsData, mapObj.LatLngBounds, mapObj.LatLng);
    mapObj.map.setCenter(center);
    mapObj.map.fitBounds(bounds);

	// Zoom out if the zoom level is greater than 14
	const zoom = mapObj.map.getZoom();
	if ( zoom > 14 ) {
		mapObj.map.setZoom(14);
	}
}

const updateMapMarkers = (mapObj, locationsData) => {
	const locationIds = locationsData.map(location => location.id);
	const mapMarkers = mapObj.markers;

	mapMarkers.forEach(marker => {
		if ( locationIds.includes(marker.id) ) {
			marker.marker.map = mapObj.map; // Set the map to make the marker visible
		} else {
			marker.marker.map = null;
		}
	});
}

const buildMapMarkerContent = (location, activeRegions, activeLocations) => {
	const content = document.createElement('div');
  
	content.setAttribute('data-marker-id', location.id);
	content.setAttribute('data-region-id', location.region);
	content.classList.add('map-marker', 'map-marker-icon', 'js-map-marker', 'js-loc-card-region');

	// Hide the marker if it's not in the active locations
	if ( activeLocations ) {
		const activeLocationsArr = activeLocations.split(',');

		// Hide the marker if it's not in the active locations
		if ( !activeLocationsArr.includes(location.id) ) {
			content.classList.add('hidden');
		}
	}

	// Hide the marker if it's not in the active regions
	if ( activeRegions ) {
		const activeRegionsArr = activeRegions.split(',');

		// Hide the marker if it's not in the active regions
		if ( !activeRegionsArr.includes(location.region) ) {
			content.classList.add('hidden');
		}
	}
	
	content.innerHTML = `
		<div class="map-marker__icon-wrapper map-marker-icon__icon-wrapper">${getMapMarkerIcon()}</div>
	`;
	return content;
}

const getMapMarkerIcon = () => {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 64 64" fill="none"><circle cx="32" cy="32" r="32" fill="#C4242D"/><path d="M38.2942 35.9157C37.7661 32.3681 36.5475 27.7508 36.3579 26.478C36.1684 25.2188 36.7506 23.6616 36.2631 20.9806C35.7757 18.2996 34.0154 16.4581 33.8394 16.1738C33.6498 15.8894 33.6498 14.6708 33.6498 14.6708C33.6092 13.6011 32.6343 12.7616 31.4834 12.8022C30.3324 12.8428 29.5065 13.6282 29.4794 14.6979C29.4794 14.6979 29.4929 15.93 29.3169 16.2144C29.1409 16.4987 27.4212 18.3673 26.9879 21.0619C26.5546 23.7564 27.164 25.2865 26.9879 26.5593C26.8254 27.8321 25.688 32.4765 25.2277 36.0241C24.7673 39.5717 24.51 43.2276 24.6996 43.7963C24.7808 44.0535 24.9027 44.0806 25.1735 44.0942C25.4443 44.0942 25.6068 44.1348 25.7287 43.7286C25.8776 43.2547 27.4754 34.5752 27.8004 32.49C28.1253 30.4048 28.5045 27.0332 29.0732 26.3156C29.6148 25.625 30.2241 25.5167 30.4407 25.1917H29.872V24.3116C29.52 24.0814 29.1679 24.0408 29.1679 24.0408L29.4523 23.6346L29.0867 23.3367L29.4387 22.9982L29.0325 22.6461L29.4523 22.2399L29.0867 21.8878L29.5742 21.5222L29.344 21.1296L29.8585 20.9129L29.696 20.439C29.696 20.439 29.9127 20.1276 30.1564 20.0328C30.4001 19.938 30.8063 19.8838 30.6845 19.5047C30.5626 19.1256 30.3189 19.112 30.2512 18.9225C30.1835 18.7329 30.6032 18.584 30.6032 18.584L30.2647 18.259L30.7793 17.9476L30.5084 17.5549L31.0907 17.3112L30.9959 16.7966L31.6188 16.1331L32.1468 16.7831L32.0656 17.2976L32.6614 17.5278L32.4041 17.9205L32.9322 18.2184L32.6072 18.5433C32.6072 18.5433 33.027 18.6923 32.9593 18.8819C32.9051 19.0714 32.6614 19.085 32.5395 19.4641C32.4177 19.8432 32.8374 19.8974 33.0811 19.9786C33.3249 20.0599 33.5415 20.3713 33.5415 20.3713L33.3926 20.8588L33.9206 21.0619L33.704 21.4545L34.1914 21.8201L33.8259 22.1857L34.2591 22.5784L33.8665 22.944L34.2185 23.2825L33.8529 23.5804L34.1373 23.9866C34.1373 23.9866 33.7582 24.0272 33.3926 24.298C33.3926 24.298 33.3655 24.3116 33.3655 24.3251V25.1917H32.8916C33.1217 25.5031 33.7311 25.6115 34.2591 26.2749C34.8414 26.979 35.2882 30.3371 35.6538 32.4223C36.0194 34.5075 37.7932 43.1599 37.9422 43.6203C38.0776 44.0265 38.2265 43.9858 38.5108 43.9858C38.7817 43.9858 38.89 43.9452 38.9712 43.688C39.1473 43.1193 38.8223 39.4633 38.2942 35.9293V35.9157ZM31.5781 15.402C31.1448 15.402 30.7928 15.0499 30.7928 14.6166C30.7928 14.1833 31.1448 13.8313 31.5781 13.8313C32.0114 13.8313 32.3635 14.1833 32.3635 14.6166C32.3635 15.0499 32.0114 15.402 31.5781 15.402Z" fill="white"/><path d="M32.3751 30.7694H31.495V29.1445H32.3751V30.7694ZM31.1836 30.7694H32.6595H31.1836ZM31.495 29.9434H32.3481H31.495Z" fill="white"/><path d="M31.1836 30.7694H32.6595M31.495 29.9434H32.3481M32.3751 30.7694H31.495V29.1445H32.3751V30.7694Z" stroke="white" stroke-width="0.384"/><path d="M30.7638 44.0402C30.7638 44.0402 30.7638 44.0267 30.7638 44.0132V41.7654C30.7638 40.7093 31.2106 39.8562 31.7929 39.7344H31.7387C31.0888 39.7344 30.5742 40.6551 30.5742 41.779V44.0267C30.5742 44.0267 30.5742 44.0402 30.5742 44.0538H30.7638V44.0402Z" fill="white"/><path d="M33.6206 44.0538C33.6206 44.0538 33.6206 44.0402 33.6206 44.0267V41.779C33.6206 40.6551 33.0925 39.7344 32.4561 39.7344H32.3207C31.6708 39.7344 31.1562 40.6551 31.1562 41.779V44.0267C31.1562 44.0267 31.1562 44.0402 31.1562 44.0538H33.6206Z" fill="white"/><path d="M32.4025 35.4139H31.5224V33.7891H32.4025V35.4139ZM31.2109 35.4139H32.6868H31.2109ZM31.5224 34.5879H32.3754H31.5224Z" fill="white"/><path d="M31.2109 35.4139H32.6868M31.5224 34.5879H32.3754M32.4025 35.4139H31.5224V33.7891H32.4025V35.4139Z" stroke="white" stroke-width="0.384"/><path d="M11.1992 47.9393C11.1992 47.9393 16.9133 45.8947 30.9547 45.9083C44.9962 45.9218 52.9715 48.0341 52.9715 48.0341C52.9715 48.0341 41.4486 47.0727 31.0766 46.9644C23.9272 46.8832 11.1992 47.9393 11.1992 47.9393Z" fill="white" stroke="white" stroke-width="0.064"/></svg>`;
}

const markerInfoWindow = (location, InfoWindow) => {
	const infowindowString = `
		<div class="map-infowindow">
			<h3 class="map-infowindow__heading">${location.title}</h3>

            <div class="map-infowindow__buttons">
                <a href="${location.url}" target="" title="Visit Location Page" class="button"><span class="f__link-text" data-label="Visit Location Page" aria-label="Link to the ${location.title} page">Discover More</span></a>
                <a href="${location.directions}" target="_blank" title="Get Directions To ${location.title}" class="button"><span class="f__link-text" data-label="Get Directions To ${location.title}" aria-label="Get Directions To ${location.title}">Get Directions</span></a>
            </div>            
		</div>
	`;

	const infowindow = new InfoWindow({
		content: infowindowString,
		ariaLabel: location.title,
	});

	return infowindow;
}

export const controlMapZoom = (mapElem, zoomType) => {
	const mapId = mapElem.getAttribute('id');
    const mapObj = maps[mapId];

    if (!mapObj) {
        console.error(`Map with ID ${mapId} not found.`);
        return;
    }

	const map = mapObj.map;
	const currentZoom = map.getZoom();

	if ( zoomType === 'in' ) {
		map.setZoom(currentZoom + 1);
	} else {
		map.setZoom(currentZoom - 1);
	}
}